import { useCallback } from 'react';
import {
	SpotMarketAccount,
	SpotMarketConfig,
	ZERO,
} from '@velocity-exchange/sdk';
import useModal from 'src/components/Modals/hooks/useModal';
import { TradeFormDetails } from 'src/components/TradeConfirmationModal/types';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useAccountData from 'src/hooks/useAccountData';
import { useOpenSpotOrder } from 'src/hooks/driftActions/spot/useOpenSpotOrder';
import { PriceImpactInfo } from 'src/utils/priceImpactUtils';
import { MarketId } from '@velocity-exchange/common';
import useOraclePrice from 'src/hooks/useOraclePrice';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import useInfoForCurrentlySelectedMarket from 'src/hooks/useInfoForCurrentlySelectedMarket';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { safeCheckDevSwitchIsOn } from 'src/hooks/useDevSwitchIsOn';

interface Params {
	setIsSubmitting: (isSubmitting: boolean) => void;
	tradeFormDetails: TradeFormDetails;
	marketIndex: number;
}

export default function useSpotTradeConfirmationModalHandlers({
	setIsSubmitting,
	tradeFormDetails,
	marketIndex,
}: Params) {
	const accountData = useAccountData();

	const userNextOrderId =
		accountData?.client?.getUserAccountOrThrow().nextOrderId;
	const currentUserKey = accountData?.userKey;

	const { captureEvent } = usePostHogCapture();
	const openSpotOrder = useOpenSpotOrder();
	const { hide } = useModal();

	const selectedMarketInfo = useInfoForCurrentlySelectedMarket();
	const selectedMarketId = selectedMarketInfo.info?.marketId as MarketId;
	const symbol = selectedMarketInfo.info?.config?.symbol as string;

	const {
		side,
		orderType,
		baseSizeStringValue,
		quoteSizeStringValue,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		reduceOnly,
		slippageTolerance,
		postOnly,
		immediateOrCancel,
		priceImpact,
		maxLeverageSelected,
	} = tradeFormDetails;

	const userCurrentPosition = useAccountTargetSpotBalance(
		selectedMarketInfo.info?.config as SpotMarketConfig,
		currentUserKey,
		true
	);
	const currentPositionBaseSize =
		userCurrentPosition?.netBaseBalance?.abs().val ?? ZERO;

	const currentPositionDirection = userCurrentPosition
		? userCurrentPosition.netBaseBalance.isNeg()
			? 'short'
			: 'long'
		: undefined;

	const spotMarketAccount = selectedMarketInfo.info
		?.account as SpotMarketAccount;

	const oraclePrice = useOraclePrice(selectedMarketId);
	const oraclePriceRef = useSyncedRef(oraclePrice);

	const handleConfirmation = useCallback(async () => {
		const isNewAccount = userNextOrderId === 1;

		setIsSubmitting(true);

		const toastId = `spot-order-${orderType}-${Date.now()}`;
		const res = await openSpotOrder(toastId, {
			side,
			baseSizeStringValue,
			orderType,
			priceBoxStringValue,
			secondaryPriceBoxStringValue,
			reduceOnly,
			slippageTolerance,
			postOnly,
			targetMarketIndex: marketIndex,
			immediateOrCancel,
			currentPositionBaseSize,
			priceImpact: priceImpact as PriceImpactInfo,
			maxLeverageSelected,
			spotMarketAccount,
			currentPositionDirection: currentPositionDirection as 'long' | 'short',
			oraclePrice: oraclePriceRef.current?.val ?? ZERO,
		});

		captureEvent('trade_placed', {
			market_type: 'spot',
			trade_base: +baseSizeStringValue,
			trade_notional: +quoteSizeStringValue,
			market_symbol: symbol,
			is_new_account: isNewAccount,
			next_order_id: userNextOrderId,
			order_type: orderType,
			geolocation_history: useDriftStore.getState().geolocationHistory,
			is_devmode: safeCheckDevSwitchIsOn(),
			direction: side,
			is_successful: res,
		});

		setIsSubmitting(false);

		if (res) {
			hide();
		}
	}, [
		userNextOrderId,
		captureEvent,
		baseSizeStringValue,
		quoteSizeStringValue,
		symbol,
		orderType,
		setIsSubmitting,
		openSpotOrder,
		side,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		reduceOnly,
		slippageTolerance,
		postOnly,
		marketIndex,
		immediateOrCancel,
		currentPositionBaseSize,
		priceImpact,
		maxLeverageSelected,
		spotMarketAccount,
		currentPositionDirection,
		oraclePriceRef,
		hide,
	]);

	return { handleConfirmation, handleClose: hide };
}
